/**
 * AddEditAudioBookV2 — full V2 port of the Audiobook edit form,
 * built on the redesign/edit primitives + SortableChildList +
 * QuotesList. Same state shape, fetch graph, and submit handler
 * as the v1 form so the API contract is byte-identical.
 *
 * Sections:
 *   01 Basic info        — title (req), description (req), parent
 *                          category (must be an audiobook category)
 *   02 Sharing & Media   — book cover, deep link image,
 *                          deep link URL
 *   03 Chapters / Sessions (edit-only) — sortable list, per-row
 *                          featured + dark-mode toggles
 *   04 Quotes            — reuse QuotesList primitive
 *   05 Danger zone (edit-only)
 *
 * Right rail:
 *   • PreviewCardAB     — wide audiobook card preview
 *   • PublishingCardAB  — type / parent / chapters / length / quotes
 *   • SettingsCardAB    — Active + Dark mode toggles
 *
 * An audiobook is technically a subcategory row with
 * subcategory_is_book=1, so we hit the same /subcategories/{id}
 * fetch and the addEditAudioBook action that wraps the same
 * subcategory write path.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  uploadImage,
  clearMedia,
} from '../../../actions/mediaActions';
import {
  fetchOneSubcategory,
  addEditAudioBook,
  deleteSubCategory,
  clearAllSubCategories,
} from '../../../actions/subcategoryActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
  LinkField,
  UploadTile,
  SortableChildList,
} from '../../redesign/edit';
import PublishingCardAB from './PublishingCardAB';
import SettingsCardAB from './SettingsCardAB';
import PreviewCardAB from './PreviewCardAB';
import QuotesList from '../../AddEditSubCategory/v2/QuotesList';
import { hiddenChapterReason, playableLengthSec } from './hiddenChapter';

const AddEditAudioBookV2 = forwardRef(function AddEditAudioBookV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─── State (1:1 with v1) ─────────────────────────────────── */
  const [state, setState] = useState({
    subcategory_id: null,
    subcategory_title: '',
    subcategory_description: '',
    subcategory_is_active: true,
    subcategory_image: '',
    subcategory_image_preview: '',
    subcategory_text_page_url: '',
    subcategory_text_image_url: '',
    subcategory_text_image_url_preview: '',
    subcategory_is_dark_mode: false,
    subcategory_is_book: true,
    category: 0,
    sessions: [],
    quotes: [],
    isSetToEdit: false,
    sessionData: {},
    sortOrder: [],
    universal_link: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const all_categories = useSelector((s) => s.categories?.all_categories);
  const current_subcategory = useSelector((s) => s.subcategories?.current_subcategory);

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(clearMedia());
    dispatch(clearAllSubCategories());
    if (isEdit) {
      dispatch(fetchOneSubcategory(selectedId));
    }
  }, [dispatch, isEdit, selectedId]);

  /* Set form state once subcategory data is loaded. */
  useEffect(() => {
    if (!isEdit || !current_subcategory) return;
    setState((prev) => {
      if (prev.isSetToEdit) return prev;
      const sessions = current_subcategory.sessions || [];
      const sessionData = {};
      const sortOrder = [];
      sessions.forEach((session) => {
        sessionData[session.session_id] = {
          is_featured: !!session.session_is_featured,
          is_dark_mode: !!session.session_is_dark_mode,
        };
        sortOrder.push(session.session_id.toString());
      });
      return {
        ...prev,
        subcategory_id: current_subcategory.subcategory_id,
        subcategory_title: current_subcategory.subcategory_title || '',
        subcategory_description: current_subcategory.subcategory_description || '',
        subcategory_is_active: !!current_subcategory.subcategory_is_active,
        subcategory_image: current_subcategory.subcategory_image || '',
        subcategory_image_preview: current_subcategory.subcategory_image || '',
        subcategory_text_page_url: current_subcategory.subcategory_text_page_url || '',
        subcategory_text_image_url: current_subcategory.subcategory_text_image_url || '',
        subcategory_text_image_url_preview: current_subcategory.subcategory_text_image_url || '',
        subcategory_is_dark_mode: !!current_subcategory.subcategory_is_dark_mode,
        subcategory_is_book: !!current_subcategory.subcategory_is_book,
        category: current_subcategory.category_id || 0,
        sessions,
        quotes: current_subcategory.quotes || [],
        universal_link: current_subcategory.universal_link || '',
        isSetToEdit: true,
        sessionData,
        sortOrder,
      };
    });

    // Hand title + updated_at up to the page chrome so it doesn't
    // need its own parallel GET /subcategories/{id}.
    if (onLoadedRef.current) {
      onLoadedRef.current({
        subcategory_title: current_subcategory.subcategory_title,
        subcategory_updated_at:
          current_subcategory.subcategory_updated_at
          ?? current_subcategory.updated_at
          ?? null,
      });
    }
  }, [isEdit, current_subcategory]);

  /* ─── Handlers (1:1 with v1) ──────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleSessionDataChange = useCallback((id, data) => {
    setState((prev) => ({
      ...prev,
      sessionData: {
        ...prev.sessionData,
        [id]: {
          is_featured: !!data.is_featured,
          is_dark_mode: !!data.is_dark_mode,
        },
      },
    }));
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (state.subcategory_image_preview) {
      window.URL.revokeObjectURL(state.subcategory_image_preview);
    }
    setState((prev) => ({
      ...prev,
      subcategory_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, subcategory_image: response.data.image_url }));
      dispatch(clearMedia());
    });
  }, [state.subcategory_image_preview, dispatch]);

  const handleDeepLinkImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (state.subcategory_text_image_url_preview) {
      window.URL.revokeObjectURL(state.subcategory_text_image_url_preview);
    }
    setState((prev) => ({
      ...prev,
      subcategory_text_image_url_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, subcategory_text_image_url: response.data.image_url }));
      dispatch(clearMedia());
    });
  }, [state.subcategory_text_image_url_preview, dispatch]);

  /* Preview-URL cleanup. */
  // Latest-value ref for the page chrome's onLoaded callback.
  // Lets the load effect stay keyed on `selectedId` alone without
  // re-firing when the parent re-renders with a fresh function.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const previewUrlsRef = useRef({});
  previewUrlsRef.current.subcategory_image_preview = state.subcategory_image_preview;
  previewUrlsRef.current.subcategory_text_image_url_preview = state.subcategory_text_image_url_preview;
  useEffect(() => {
    return () => {
      const refs = previewUrlsRef.current;
      if (refs.subcategory_image_preview) window.URL.revokeObjectURL(refs.subcategory_image_preview);
      if (refs.subcategory_text_image_url_preview) window.URL.revokeObjectURL(refs.subcategory_text_image_url_preview);
    };
  }, []);

  /* ─── Submit (1:1 with v1) ─────────────────────────────── */
  const submit = useCallback(() => {
    if (!state.subcategory_title) {
      Swal.fire({ icon: 'error', text: 'Audiobook name is required', confirmButtonText: 'Ok' });
      return;
    }
    if (!state.subcategory_description) {
      Swal.fire({ icon: 'error', text: 'Audiobook description is required', confirmButtonText: 'Ok' });
      return;
    }
    setIsSaving(true);

    const {
      subcategory_id,
      subcategory_title,
      subcategory_description,
      subcategory_is_active,
      subcategory_image,
      subcategory_text_page_url,
      subcategory_text_image_url,
      subcategory_is_dark_mode,
      subcategory_is_book,
      quotes,
      category: category_id,
      sessionData,
      sortOrder,
    } = state;

    const subcategory_json = {
      subcategory_id,
      subcategory_title,
      subcategory_description,
      subcategory_is_active,
      subcategory_image,
      subcategory_text_page_url,
      subcategory_text_image_url,
      subcategory_is_dark_mode,
      subcategory_is_book,
    };

    const filteredQuotes = (quotes || [])
      .filter((q) => q.quote_text !== '')
      .map((q) => ({ quote_text: q.quote_text, speaker: q.speaker || '' }));

    const formattedSessions = (sortOrder || []).map((sessionId) => ({
      session_id: sessionId,
      session_is_featured: !!sessionData[sessionId]?.is_featured,
      session_is_dark_mode: !!sessionData[sessionId]?.is_dark_mode,
    }));

    const options = {
      quotes_json: JSON.stringify(filteredQuotes),
      category_id,
      subcategory_json: JSON.stringify(subcategory_json),
      session_ids_json: JSON.stringify(formattedSessions),
    };

    // `addEditAudioBook` doesn't return a promise from the inner
    // dispatch function (the action thunk fires-and-forgets the
    // apiV2 call without returning it), so dispatch() resolves to
    // undefined. Bare `.finally()` on that throws TypeError and
    // crashes the save flow — see the same guard in AddEditAuthorV2.
    const result = dispatch(addEditAudioBook(options, () => navigate('/audiobooks')));
    if (result && typeof result.finally === 'function') {
      result.finally(() => setIsSaving(false));
    } else {
      setTimeout(() => setIsSaving(false), 3000);
    }
  }, [state, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete (1:1 with v1) ──────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this audiobook?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete Audiobook',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteSubCategory(state.subcategory_id, () => navigate('/audiobooks')));
      }
    });
  };

  /* ─── Derived data ──────────────────────────────────────── */
  const categoryOptions = useMemo(() => {
    if (!all_categories) return [];
    const list = Array.isArray(all_categories) ? all_categories : Object.values(all_categories);
    return list
      .filter((c) => c && c.category_id && c.category_is_audiobook)
      .map((c) => ({
        value: String(c.category_id),
        label: c.category_title || 'Untitled',
      }));
  }, [all_categories]);

  const parentCategoryTitle = useMemo(() => {
    if (!all_categories || !state.category) return null;
    const list = Array.isArray(all_categories) ? all_categories : Object.values(all_categories);
    const hit = list.find((c) => c.category_id === state.category);
    return hit?.category_title || null;
  }, [all_categories, state.category]);

  // Build sortable items + total length from the underlying sessions.
  // `kind` flips the row chrome in SortableChildList: chapters render
  // full-width with a bolder uppercase title + "Chapter" pill;
  // sessions indent and show a 32px thumbnail + "Tapping meditation"
  // eyebrow. Mirrors TheTappingSolutionWeb's `AudiobookSessionItem`
  // hierarchy on /audiobooks/{id}.
  const sortableItems = useMemo(() => {
    return (state.sortOrder || []).map((sessionId) => {
      const session = (state.sessions || []).find(
        (s) => s.session_id.toString() === sessionId.toString(),
      );
      return {
        id: sessionId.toString() + 'SESSION',
        label: session?.session_name || `Session ${sessionId}`,
        // SortableChildList strips digits from `id` to key sessionData
        // — match that with `value` matching the bare session_id so
        // featured/dark toggles route through correctly.
        value: sessionId.toString(),
        kind: session?.session_is_chapter ? 'chapter' : 'session',
        imageUrl: session?.session_image || '',
        // Linked but not listed in the app (inactive).
        hiddenReason: hiddenChapterReason(session),
      };
    });
  }, [state.sortOrder, state.sessions]);

  const hiddenCount = sortableItems.filter((i) => i.hiddenReason).length;
  // Chapters the app lists; the Publishing card counts these, matching its
  // Length, which also skips hidden chapters.
  const inAppCount = sortableItems.length - hiddenCount;

  // Total length — sum session_length_in_sec across the chapters the
  // app plays (hidden ones excluded). Backed by api#579 which extended
  // SubcategoryResource to ship session_length_in_sec on each session row.
  const totalLengthSec = useMemo(() => playableLengthSec(state.sessions), [state.sessions]);

  // Aggregate unique contributor names across all chapters.
  // Mirrors the WebApp's audiobook listing which shows
  // "by …" with the chapter author(s). Two authors → "A & B",
  // three+ → "A, B & 2 more" so the line stays compact.
  // Backed by api#579 which exposes author_name on each session row.
  const authorName = useMemo(() => {
    const names = [];
    const seen = new Set();
    (state.sessions || []).forEach((s) => {
      const n = s?.author_name || s?.author?.author_name;
      if (n && !seen.has(n)) {
        seen.add(n);
        names.push(n);
      }
    });
    if (names.length === 0) return null;
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names[0]}, ${names[1]} & ${names.length - 2} more`;
  }, [state.sessions]);

  /* ─── Sort handler — adapts SortableChildList's arrayMove
   * output back into v1's `sortOrder` array of bare session ids. */
  const onSortEnd = (newIdOrderWithSuffix) => {
    const stripped = newIdOrderWithSuffix.map((id) => id.replace(/SESSION$/, ''));
    setState((prev) => ({ ...prev, sortOrder: stripped }));
  };

  const onRemoveAt = (index) => {
    setState((prev) => ({
      ...prev,
      sortOrder: prev.sortOrder.filter((_, i) => i !== index),
    }));
  };

  /* ─── Render ────────────────────────────────────────────── */
  if (!all_categories) {
    return <LoadingBackdrop open />;
  }
  if (isEdit && !state.isSetToEdit) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
        <form
          id="audiobook-form"
          onSubmit={handleSubmit}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* 01 Basic info */}
          <FormSection title="Basic info" num="01">
            <FieldBlock label="Audiobook name" required htmlFor="f-ab-title">
              <TextInput
                id="f-ab-title"
                name="subcategory_title"
                value={state.subcategory_title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Description" required htmlFor="f-ab-desc">
              <Textarea
                id="f-ab-desc"
                name="subcategory_description"
                value={state.subcategory_description}
                onChange={handleFieldChange}
                rows={3}
                placeholder="Shown on the audiobook landing page."
                required
              />
            </FieldBlock>
            <FieldBlock
              label="Parent category"
              required
              hint="Must be a category flagged as an audiobook category."
              noMargin
            >
              <Select
                value={state.category ? String(state.category) : '0'}
                onChange={(e) => setField('category', e.target.value === '0' ? 0 : Number(e.target.value))}
              >
                <option value="0">— pick a category —</option>
                {categoryOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FieldBlock>
          </FormSection>

          {/* 02 Sharing & Media */}
          <FormSection title="Sharing & Media" num="02">
            <FieldRow cols={2} minColWidth={240}>
              <UploadTile
                label="Audiobook cover"
                required
                hint="Square book cover"
                value={state.subcategory_image_preview || state.subcategory_image}
                accept="image/*"
                onChange={handleImageUpload}
                onRemove={() => {
                  if (state.subcategory_image_preview) window.URL.revokeObjectURL(state.subcategory_image_preview);
                  setState((prev) => ({ ...prev, subcategory_image: '', subcategory_image_preview: '' }));
                }}
              />
              <UploadTile
                label="Deep Link Image"
                hint="Social share card"
                value={state.subcategory_text_image_url_preview || state.subcategory_text_image_url}
                accept="image/*"
                onChange={handleDeepLinkImageUpload}
                onRemove={() => {
                  if (state.subcategory_text_image_url_preview) window.URL.revokeObjectURL(state.subcategory_text_image_url_preview);
                  setState((prev) => ({ ...prev, subcategory_text_image_url: '', subcategory_text_image_url_preview: '' }));
                }}
              />
            </FieldRow>
            <div style={{ marginTop: 16 }}>
              <FieldBlock
                label="Deep Link URL"
                hint="External preview / sample page URL used for share cards"
                htmlFor="f-ab-deep-url"
                noMargin={!isEdit || !state.universal_link}
              >
                <TextInput
                  id="f-ab-deep-url"
                  name="subcategory_text_page_url"
                  value={state.subcategory_text_page_url || ''}
                  onChange={handleFieldChange}
                  placeholder="https://…"
                />
              </FieldBlock>
            </div>
            {isEdit && state.universal_link && (
              <div style={{ marginTop: 16 }}>
                <FieldBlock
                  label="Universal Link"
                  hint="Generated automatically · used for share & deep links"
                  noMargin
                >
                  <LinkField value={state.universal_link} />
                </FieldBlock>
              </div>
            )}
          </FormSection>

          {/* 03 Chapters / Sessions (edit-only) */}
          {isEdit && (
            <FormSection
              title="Chapters / Sessions"
              num="03"
              right={
                <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                  {hiddenCount > 0
                    ? `${inAppCount} in app · ${hiddenCount} hidden`
                    : sortableItems.length}
                </span>
              }
            >
              <SortableChildList
                items={sortableItems}
                sortOrder={sortableItems.map((i) => i.id)}
                onRemove={onRemoveAt}
                onSortEnd={onSortEnd}
                showSwitches
                onSessionDataChange={handleSessionDataChange}
                sessionData={state.sessionData}
                emptyText="No chapters yet — attach sessions from the Sessions list."
              />
            </FormSection>
          )}

          {/* 04 Quotes */}
          <FormSection
            title="Quotes"
            num={isEdit ? '04' : '03'}
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {(state.quotes || []).length}
              </span>
            }
          >
            <QuotesList
              quotes={state.quotes}
              onChange={(nextQuotes) => setField('quotes', nextQuotes)}
            />
          </FormSection>

          {/* 05 Danger zone */}
          {isEdit && (
            <FormSection title="Danger zone" num="05">
              <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Button variant="danger" onClick={handleDelete} type="button">
                  Delete audiobook
                </Button>
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Permanent — cannot be undone.
                </span>
              </div>
            </FormSection>
          )}
        </form>

        {/* Right rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <PreviewCardAB
            state={state}
            totalLengthSec={totalLengthSec}
            authorName={authorName}
          />
          {isEdit && (
            <PublishingCardAB
              state={state}
              parentCategoryTitle={parentCategoryTitle}
              chapterCount={inAppCount}
              totalLengthSec={totalLengthSec}
              quoteCount={(state.quotes || []).length}
              onToggleActive={(next) => setField('subcategory_is_active', !!next)}
            />
          )}
          <SettingsCardAB
            state={state}
            onChange={(name, value) => setField(name, !!value)}
          />
        </aside>
      </div>
    </>
  );
});

export default AddEditAudioBookV2;
